import {
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import html2pdf from "html2pdf.js";
import { useEffect, useMemo, useRef, useState } from "react";
import Flex from "../../../../../../components/Flex";
import StyledDialog from "../../../../../../components/StyledDialog";
import Text from "../../../../../../components/Text";
import LoadingOverlay from "../../../../../../components/LoadingOverlay";
import { DisclosureHost } from "../../../../../../hooks/use-disclosure";
import { useBulkScrubClaims } from "../../../../../../queries/billings/claims/use-bulk-scrub-claims";
import { useGetClaimsByUuids } from "../../../../../../queries/billings/claims/use-get-claims-by-uuids";
import { useSnackbar } from "../../../../../../hooks/useSnackbar";
import { getErrorSnackbar } from "../../../../../../utils/utils";
import ScrubResultsPdfTemplate from "./ScrubResultsPdfTemplate";

interface ScrubClaimsModalProps {
  disclosureHost: DisclosureHost;
  claimUuids: string[];
}

interface ScrubItem {
  claimUuid: string;
  passed: boolean;
  errors: { field?: string; message?: string }[];
  error?: string | null;
}

interface ScrubResponseData {
  totalCount: number;
  passedCount: number;
  failedCount: number;
  notFoundCount: number;
  items: ScrubItem[];
}

// Matches A4 landscape usable width at 96dpi minus 0.4" margins (≈ 1085px).
// Keeping a small safety buffer prevents right-edge clipping in the PDF.
const PDF_TEMPLATE_WIDTH = 1040;

const SUMMARY_CARD_SX = {
  borderRadius: 2,
  p: 2,
  textAlign: "center" as const,
  height: "100%",
  display: "flex",
  flexDirection: "column" as const,
  justifyContent: "center",
};

const SUMMARY_CARDS: {
  label: string;
  border: string;
  color: string;
  value: (r: ScrubResponseData, totalIssues: number) => number;
}[] = [
  {
    label: "Claims Validated",
    border: "#90CAF9",
    color: "#1976d2",
    value: (r) => r.totalCount ?? 0,
  },
  {
    label: "Claims with Errors",
    border: "#EF5350",
    color: "#d32f2f",
    value: (r) => r.failedCount ?? 0,
  },
  {
    label: "Clean Claims",
    border: "#66BB6A",
    color: "#2e7d32",
    value: (r) => r.passedCount ?? 0,
  },
  {
    label: "Total Issues Found",
    border: "#FFA726",
    color: "#f57c00",
    value: (_r, totalIssues) => totalIssues,
  },
];

const StatusChip = ({ passed }: { passed: boolean }) => {
  const colors = passed
    ? { color: "#00B917", bgColor: "#E5F8E7", label: "Passed" }
    : { color: "#FF0000", bgColor: "#FFE5E5", label: "Failed" };

  return (
    <Chip
      label={colors.label}
      size="small"
      sx={{
        color: colors.color,
        bgcolor: colors.bgColor,
        fontWeight: 500,
        fontSize: "0.75rem",
        height: "24px",
      }}
    />
  );
};

const ScrubClaimsModal = ({
  disclosureHost,
  claimUuids,
}: ScrubClaimsModalProps) => {
  const showSnackbar = useSnackbar();
  const pdfTemplateRef = useRef<HTMLDivElement>(null);

  const [scrubResult, setScrubResult] = useState<ScrubResponseData | null>(
    null
  );
  const [errorModalRow, setErrorModalRow] = useState<{
    claimId: string;
    patientName: string;
    errors: { field?: string; message?: string }[];
  } | null>(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);

  const openErrorModal = (row: {
    claimId: string;
    patientName: string;
    errors: { field?: string; message?: string }[];
  }) => {
    setErrorModalRow(row);
    setErrorModalOpen(true);
  };

  const closeErrorModal = () => setErrorModalOpen(false);

  const { mutateAsync: bulkScrubClaims, isPending: isScrubbing } =
    useBulkScrubClaims();
  const { data: claimsData, isLoading: isClaimsLoading } =
    useGetClaimsByUuids(claimUuids);

  useEffect(() => {
    if (!disclosureHost.opened || claimUuids.length === 0) return;
    bulkScrubClaims({ bulkScrubRequest: { claimUuids } })
      .then((res) => {
        setScrubResult(res?.data?.data as ScrubResponseData);
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disclosureHost.opened]);

  const claimMetaByUuid = useMemo(() => {
    const raw = claimsData as unknown;
    const list = Array.isArray(raw)
      ? raw
      : Array.isArray((raw as { data?: unknown })?.data)
        ? (raw as { data: unknown[] }).data
        : [];
    const map = new Map<string, { claimId: string; patientName: string }>();
    (list as Array<{ uuid?: string; patientName?: string }>).forEach((c) => {
      if (c?.uuid) {
        map.set(c.uuid, {
          claimId: c.uuid.split("-")?.[0]?.toUpperCase() ?? "",
          patientName: c?.patientName ?? "-",
        });
      }
    });
    return map;
  }, [claimsData]);

  const tableData = useMemo(() => {
    if (!scrubResult?.items) return [];
    return scrubResult.items.map((item) => {
      const meta = claimMetaByUuid.get(item.claimUuid);
      return {
        claimUuid: item.claimUuid,
        claimId:
          meta?.claimId ?? item.claimUuid?.split("-")?.[0]?.toUpperCase(),
        patientName: meta?.patientName ?? "-",
        passed: item.passed,
        errorsFound: item.errors?.length ?? 0,
        errors: item.errors ?? [],
      };
    });
  }, [scrubResult, claimMetaByUuid]);

  const closeModal = () => {
    disclosureHost.close();
  };

  const handleExportResults = async () => {
    if (!pdfTemplateRef.current) return;
    const options = {
      margin: [0.4, 0.4, 0.5, 0.4],
      filename: `Claims_Scrub_Summary_${new Date().getTime()}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        scrollX: 0,
        scrollY: 0,
        windowWidth: PDF_TEMPLATE_WIDTH,
        width: PDF_TEMPLATE_WIDTH,
      },
      jsPDF: { unit: "in", format: "a4", orientation: "landscape" },
      pagebreak: { mode: ["css", "legacy"], avoid: "tr, li" },
    };
    try {
      await html2pdf().from(pdfTemplateRef.current).set(options).save();
    } catch (err) {
      showSnackbar({
        message: "Failed to export results",
        type: "error",
      });
    }
  };

  const isLoading = isScrubbing || isClaimsLoading;

  const totalCount = scrubResult?.totalCount ?? 0;
  const failedCount = scrubResult?.failedCount ?? 0;
  const passedCount = scrubResult?.passedCount ?? 0;
  const totalIssues = useMemo(
    () =>
      scrubResult?.items?.reduce(
        (acc, item) => acc + (item.errors?.length ?? 0),
        0
      ) ?? 0,
    [scrubResult]
  );

  return (
    <StyledDialog
      open={disclosureHost.opened}
      onClose={closeModal}
      dialogTitle="Claims Validation"
      dialogAction={
        <Flex gap={2}>
          <Button
            variant="outlined"
            onClick={handleExportResults}
            disabled={!scrubResult || tableData.length === 0}
          >
            Export Results
          </Button>
        </Flex>
      }
      sx={{
        "& .MuiDialog-paper": {
          maxWidth: "1200px",
          width: "100%",
        },
      }}
    >
      <Box width={"100%"} sx={{ position: "relative" }}>
        <LoadingOverlay isLoading={isLoading} />
        {!isLoading && scrubResult && (
          <Box>
            {/* Summary Cards Section */}
            <Grid container spacing={2} mb={3}>
              {SUMMARY_CARDS.map((card) => (
                <Grid item xs={3} key={card.label}>
                  <Paper
                    elevation={0}
                    sx={{
                      ...SUMMARY_CARD_SX,
                      border: `1px solid ${card.border}`,
                    }}
                  >
                    <Text fontSize="2rem" fontWeight={700} color={card.color}>
                      {String(card.value(scrubResult, totalIssues)).padStart(
                        2,
                        "0"
                      )}
                    </Text>
                    <Text fontSize="0.9rem" color="text.secondary">
                      {card.label}
                    </Text>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Detailed Results Table */}
            <Box>
              <Text fontSize="1.1rem" fontWeight={600} mb={2}>
                Detailed results
              </Text>
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Claim ID</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Patient Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Errors Found
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Errors</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableData.map((row) => (
                      <TableRow key={row.claimUuid} hover>
                        <TableCell>{row.claimId}</TableCell>
                        <TableCell>{row.patientName}</TableCell>
                        <TableCell>
                          <StatusChip passed={row.passed} />
                        </TableCell>
                        <TableCell>
                          {row.errorsFound > 0
                            ? String(row.errorsFound).padStart(2, "0")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            title={
                              row.errors.length > 0
                                ? "View errors"
                                : "No errors"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                disabled={row.errors.length === 0}
                                onClick={() =>
                                  openErrorModal({
                                    claimId: row.claimId ?? "",
                                    patientName: row.patientName,
                                    errors: row.errors,
                                  })
                                }
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {tableData.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Text color="text.secondary">No results found</Text>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        )}
      </Box>

      {/* Hidden PDF template rendered off-screen, used as html2pdf source */}
      <Box
        sx={{
          position: "fixed",
          left: "-10000px",
          top: 0,
          width: `${PDF_TEMPLATE_WIDTH}px`,
          pointerEvents: "none",
          opacity: 0,
        }}
        aria-hidden
      >
        {scrubResult && (
          <ScrubResultsPdfTemplate
            ref={pdfTemplateRef}
            width={PDF_TEMPLATE_WIDTH}
            totalCount={totalCount}
            passedCount={passedCount}
            failedCount={failedCount}
            notFoundCount={scrubResult.notFoundCount ?? 0}
            totalIssues={totalIssues}
            rows={tableData.map((r) => ({
              claimUuid: r.claimUuid,
              claimId: r.claimId ?? "",
              patientName: r.patientName,
              passed: r.passed,
              errorsFound: r.errorsFound,
              errors: r.errors,
            }))}
          />
        )}
      </Box>

      <StyledDialog
        open={errorModalOpen}
        onClose={closeErrorModal}
        TransitionProps={{
          onExited: () => setErrorModalRow(null),
        }}
        dialogTitle={
          errorModalRow
            ? `Errors — ${errorModalRow.patientName} (${errorModalRow.claimId})`
            : "Errors"
        }
        dialogAction={
          <Button variant="outlined" onClick={closeErrorModal}>
            Close
          </Button>
        }
        sx={{
          "& .MuiDialog-paper": {
            maxWidth: "640px",
            width: "100%",
          },
        }}
      >
        <Box width={"100%"}>
          {errorModalRow && errorModalRow.errors.length > 0 ? (
            <Box component="ul" sx={{ pl: 3, m: 0 }}>
              {errorModalRow.errors.map((err, idx) => (
                <Box
                  component="li"
                  key={idx}
                  sx={{
                    fontSize: "0.9rem",
                    color: "#d32f2f",
                    mb: 1,
                  }}
                >
                  {err.field && (
                    <Text component="span" fontWeight={600} mr={0.5}>
                      {err.field}:
                    </Text>
                  )}
                  {err.message ?? ""}
                </Box>
              ))}
            </Box>
          ) : (
            <Text color="text.secondary">No errors found.</Text>
          )}
        </Box>
      </StyledDialog>
    </StyledDialog>
  );
};

export default ScrubClaimsModal;
